// src/components/AccessAssignment.jsx
import React, { useEffect, useState } from 'react';
import { collection, query, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getCurrentUserCompanyId } from '../utils/userUtils';
import { useTranslation } from 'react-i18next';

export default function AccessAssignment({ 
  assignedUsers = [], 
  assignedGroups = [], 
  allUsers = false, 
  allGroups = false, 
  onChange 
}) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { t: translate } = useTranslation();

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentCompanyId = await getCurrentUserCompanyId();
        if (!currentCompanyId) {
          setLoading(false);
          return;
        }
        
        setCompanyId(currentCompanyId);

        // Load company data to get groups
        const companyDoc = await getDoc(doc(db, 'companies', currentCompanyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          setGroups(companyData.groups || []);
        }

        // Subscribe to users in real-time from top-level users collection
        const usersQuery = query(collection(db, 'users'));
        const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
          const usersList = snapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(user => 
              user.companyId === currentCompanyId && 
              user.active !== false &&
              user.status !== 'pending'
            );
          setUsers(usersList);
          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error loading access assignment data:', error);
        setLoading(false);
      }
    };

    const unsubscribe = loadData();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const handleUserToggle = (userId) => {
    const newAssignedUsers = assignedUsers.includes(userId)
      ? assignedUsers.filter(id => id !== userId)
      : [...assignedUsers, userId];
    
    onChange({
      assignedUsers: newAssignedUsers,
      assignedGroups,
      allUsers,
      allGroups
    });
  };

  const handleGroupToggle = (groupName) => {
    const newAssignedGroups = assignedGroups.includes(groupName)
      ? assignedGroups.filter(name => name !== groupName)
      : [...assignedGroups, groupName];
    
    onChange({
      assignedUsers,
      assignedGroups: newAssignedGroups,
      allUsers,
      allGroups
    });
  };

  const handleAllUsersToggle = () => {
    const newAllUsers = !allUsers;
    onChange({
      assignedUsers: newAllUsers ? [] : assignedUsers, // Clear individual selections if "All Users" is selected
      assignedGroups,
      allUsers: newAllUsers,
      allGroups
    });
  };

  const handleAllGroupsToggle = () => {
    const newAllGroups = !allGroups;
    onChange({
      assignedUsers,
      assignedGroups: newAllGroups ? [] : assignedGroups, // Clear individual selections if "All Groups" is selected
      allUsers,
      allGroups: newAllGroups
    });
  };

  if (loading) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="text-sm text-gray-500">{translate('formBuilder.loadingUsersGroups')}</div>
      </div>
    );
  }

  return (
    <div className="border border-white/25 bg-slate-900/60 text-white rounded-lg p-4 space-y-4 backdrop-blur-md shadow-sm">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
          <h3 className="font-semibold text-white">{translate('formBuilder.formAccessControl')}</h3>
        </div>
        <button className="text-white hover:text-white/80 transition-transform">
          {isCollapsed ? '▼' : '▲'}
        </button>
      </div>
      
      {!isCollapsed && (
        <>
          <div className="text-sm text-white/90">
            {translate('formBuilder.accessDescription')}
          </div>

          {/* All Users Toggle */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-white">
              <input
                type="checkbox"
                checked={allUsers}
                onChange={handleAllUsersToggle}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="font-medium text-green-700 dark:text-green-400">✓ {translate('formBuilder.allUsersLabel')}</span>
            </label>
            
            {!allUsers && users.length > 0 && (
              <div className="ml-6 space-y-1 max-h-32 overflow-y-auto text-white/90">
                <div className="text-xs font-medium text-white/70 uppercase tracking-wide">{translate('formBuilder.individualUsers')}</div>
                {users.map((user) => (
                  <label key={user.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={assignedUsers.includes(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="truncate text-white">
                      {user.displayName || user.name || user.email} 
                      {user.role === 'admin' && <span className="text-xs text-rose-200"> ({translate('formBuilder.adminTag')})</span>}
                      {user.group && <span className="text-xs text-white/70"> - {user.group}</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {!allUsers && users.length === 0 && (
              <div className="ml-6 text-xs text-white/70">
                {translate('formBuilder.noUsersFound')}
              </div>
            )}
          </div>

          {/* All Groups Toggle */}
          {groups.length > 0 && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-white">
                <input
                  type="checkbox"
                  checked={allGroups}
                  onChange={handleAllGroupsToggle}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="font-medium text-green-700 dark:text-green-400">✓ {translate('formBuilder.allGroupsLabel')}</span>
              </label>
              
              {!allGroups && (
                <div className="ml-6 space-y-1">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{translate('formBuilder.individualGroups')}</div>
                  {groups.map((group) => (
                    <label key={group} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={assignedGroups.includes(group)}
                        onChange={() => handleGroupToggle(group)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="truncate">{group}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="mt-4 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">{translate('formBuilder.accessSummary')}</div>
            {allUsers && allGroups ? (
              <div className="text-green-600 dark:text-green-400">✓ {translate('formBuilder.everyoneCan')}</div>
            ) : allUsers ? (
              <div className="text-green-600 dark:text-green-400">✓ {translate('formBuilder.allUsersCan')}</div>
            ) : allGroups ? (
              <div className="text-green-600 dark:text-green-400">✓ {translate('formBuilder.allGroupsCan')}</div>
            ) : (
              <div className="text-gray-600 dark:text-gray-400">
                {assignedUsers.length + assignedGroups.length === 0 ? (
                  <span className="text-amber-600 dark:text-amber-400">⚠️ {translate('formBuilder.noOneAssigned')}</span>
                ) : (
                  <>
                    {assignedUsers.length > 0 && <div>• {assignedUsers.length} {assignedUsers.length !== 1 ? translate('formBuilder.specificUsers') : translate('formBuilder.specificUser')}</div>}
                    {assignedGroups.length > 0 && <div>• {assignedGroups.length} {assignedGroups.length !== 1 ? translate('formBuilder.specificGroups') : translate('formBuilder.specificGroup')}</div>}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}